import React, { useState, useEffect, useMemo, useRef } from 'react'
import PageWrapper from 'components/Common/PageWraper';
import { Box, Grid, Stack, Typography, useTheme } from '@mui/material';
import SimpleDateFilter from 'components/Dropdown/SimpleDateFilter';
import YearMonthPicker from 'components/Dropdown/YearMonthPicker';
import moment from 'moment';
import KpiCard from 'components/TableauObs/common/KpiCard';
import MultiLineChart from 'components/Widgets/MultiLineChart';
import { renderTooltipIconComponent } from 'components/TableauObs/common/PieChart';
import ReactECharts from 'echarts-for-react';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import DialogueModal from 'components/Modal/DialogModal';
import { CustomizedDataGrid } from 'components/customized_data_grid';
import WidgetWraper from 'components/Common/WidgetWraper';
import { InfoTooltip } from 'components/Widgets/InfoTooltip';
import {
  fetchProductSuccessKPIs, fetchProductSuccessUsersRoleUsageTrend, fetchProductSuccessMostVisitedPage,
  fetchProductSuccessUserAvgTimeInPlatform, fetchProductSuccessUserAdoptionRate, fetchProductSuccessAdoptionRateTrend
} from 'service/service';

const DEFAULT_TIME_RANGE = 'monthly';

const TIME_FILTER_OPTIONS = [	
	{ value: 'weekly', label: 'Weekly' },
	{ value: 'monthly', label: 'Monthly' },
	{ value: 'yearly', label: 'Yearly' }
];

const getInitialMonth = () => {
    return moment().subtract(1, 'month').format('YYYY-MM') ;
};

// ─── Shared chart config helpers ─────────────────────────────────────────────

const getHorizontalBarTooltip = (formatter: (params: any) => string) => ({
  trigger: 'axis' as const,
  axisPointer: { type: 'shadow' as const },
  backgroundColor: 'rgba(0, 0, 0, 0.8)',
  borderColor: '#777',
  borderWidth: 1,
  textStyle: { color: '#fff' },
  formatter,
});

const HORIZONTAL_BAR_GRID = {
  left: '3%',
  right: '10%',
  bottom: '3%',
  top: '3%',
  containLabel: true,
};

const HORIZONTAL_BAR_X_AXIS = {
  type: 'value' as const,
  axisLine: { lineStyle: { color: 'rgba(0, 0, 0, 0.3)' } },
  axisLabel: { color: '#000000' },
  splitLine: { lineStyle: { color: 'rgba(0, 0, 0, 0.1)' } },
};

const getHorizontalBarYAxis = (data: string[]) => ({
  type: 'category' as const,
  data,
  axisLine: { lineStyle: { color: 'rgba(0, 0, 0, 0.3)' } },
  axisLabel: { color: '#000000', fontSize: 11 },
});

// ─────────────────────────────────────────────────────────────────────────────

// ─── Most Visited Page display name overrides ───────────────────────────────
// FullStory's PageCategory values don't match the labels stakeholders want shown.
const PAGE_NAME_DISPLAY_OVERRIDES: Record<string, string> = {
  'Dashboard': 'Landing Page',
  'Data Pipeline': 'Data Pipeline Observabilities',
};

const toPageDisplayName = (pageName: string): string =>
  PAGE_NAME_DISPLAY_OVERRIDES[pageName] ?? pageName;

// ─── Active Users Modal Static Data ─────────────────────────────────────────

const ACTIVE_USERS_COLUMNS = [
  { key: 'userName', name: 'USER NAME', width: 400, minWidth: 200, resizable: true },
  { key: 'activeTimeInPlatform', name: 'ACTIVE TIME IN PLATFORM', width: 400, minWidth: 250, resizable: true },
  { key: 'role', name: 'ROLE', width: 250, minWidth: 200, resizable: true },
];

const ACTIVE_USERS_DATA = [
  { id: '1', userName: 'Kurnool, Latha',           activeTimeInPlatform: '38 m 52 s', role: 'Data Eng Director' },
  { id: '2', userName: 'Yerramsetti, Kusuma',       activeTimeInPlatform: '18 m 2 s',  role: 'Data Eng Assoc Prin Engineer' },
  { id: '3', userName: 'Swaminathan, Saravanan',    activeTimeInPlatform: '23 m 12 s', role: 'Data Analytics Platforms' },
  { id: '4', userName: 'Kakiveli, Shankar',         activeTimeInPlatform: '8 m 3 s',   role: 'Data Eng Assoc Prin Engineer' },
  { id: '5', userName: 'Bheemanapalli, Sreedevi',   activeTimeInPlatform: '24 m 45 s', role: 'Data Eng Principal Engineer' },
  { id: '6', userName: 'Aguilar, Alan',             activeTimeInPlatform: '13 m 11 s', role: 'Data Analytics Platforms' },
];

// ─────────────────────────────────────────────────────────────────────────────
interface RoleUserItem {
  indvId: number;
  email: string | null;
  displayName: string;
}

interface RoleDataItem {
  role: string;
  count: number;
  users?: RoleUserItem[];
}

const ProductSuccessDashboard = () => {
  const theme = useTheme();
  const [filterMonth, setFilterMonth] = useState(getInitialMonth());
  const [customMonth, setCustomMonth] = useState(getInitialMonth());
  const [timeRange, setTimeRange] = useState(DEFAULT_TIME_RANGE);
  const [activeUsersModalOpen, setActiveUsersModalOpen] = useState(false);
  const [kpiData, setKpiData] = useState<any>(null);
  const [kpiLoading, setKpiLoading] = useState(true);
  const [userRoleUsageChartLoading, setUserRoleUsageChartLoading] = useState(true);
  const [roleData, setRoleData] = useState<RoleDataItem[]>([]);
  const [mostVisitedPageChartLoading, setMostVisitedPageChartLoading] = useState(true);
  const [mostVisitedPageData, setMostVisitedPageData] = useState<any>([]);
  const [expandedPage, setExpandedPage] = useState<string | null>(null);
  const mostVisitedChartRef = useRef<any>(null);
  const mostVisitedOverlayRef = useRef<HTMLDivElement>(null);
  const [rowPixelPositions, setRowPixelPositions] = useState<Record<string, number>>({});
  const [expandedRole, setExpandedRole] = useState<string | null>(null);
  const roleChartRef = useRef<any>(null);
  const roleOverlayRef = useRef<HTMLDivElement>(null);
  const [rolePixelPositions, setRolePixelPositions] = useState<Record<string, number>>({});

  const [avgSessionData, setAvgSessionData] = useState<any>(null);
  const [loadingAvgSession, setLoadingAvgSession] = useState(true);
  const [adoptionRate, setAdoptionRate] = useState<any>(null);
  const [loadingAdoptionRate, setLoadingAdoptionRate] = useState(true);
  const [adoptionTrendData, setAdoptionTrendData] = useState<any[]>([]);
  const [adoptionTrendLoading, setAdoptionTrendLoading] = useState(true);

  const handleDateChange = (selectedDate: string) => {
		setTimeRange(selectedDate);
	};

  const showTimeRange = (value: string) => {
		const match = TIME_FILTER_OPTIONS.find(option => option.value === value);
		return match ? match.label : value;
	};

  const handleFilterChange = (selectedDate: string) => {
      setFilterMonth(selectedDate);
  };

  useEffect(() => {
    setAdoptionTrendLoading(true);
    fetchProductSuccessAdoptionRateTrend()
      .then((res) => {
        if (res?.data?.trend) {
          setAdoptionTrendData(res.data.trend);
        }
      })
      .catch(() => {})
      .finally(() => setAdoptionTrendLoading(false));
  }, []);

  useEffect(() => {
    setKpiLoading(true);
    fetchProductSuccessKPIs(timeRange, filterMonth)
      .then((res) => {
        if (res?.data) setKpiData(res.data);
      })
      .catch(() => {})
      .finally(() => setKpiLoading(false));
    fetchUserRoleUsageChartData(timeRange, filterMonth);
    fetchUserAvgTimeInPlatform(timeRange, filterMonth);
    fetchUserAdoptionRate(timeRange, filterMonth);
    fetchUserMostVisitedPageChartData(timeRange, filterMonth);
  }, [timeRange, filterMonth]);

  // User Engagement Chart Configuration
  const userEngagementData = {
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'shadow'
      },
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      borderColor: '#777',
      borderWidth: 1,
      textStyle: {
        color: '#fff'
      }
    },
    legend: {
      data: ['Total Users', 'New Users'],
      show: true,
      type: 'scroll',
      orient: 'horizontal',
      left: 'center',
      bottom: '8px',
      itemWidth: 14,
      itemHeight: 14,
      itemGap: 15,
      icon: 'roundRect',
      textStyle: {
        color: '#000000',
        fontWeight: 'medium'
      }
    },
    grid: {
      left: '3%',
      right: '4%',
      bottom: '17%',
      top: '10%',
      containLabel: true
    },
    xAxis: {
      type: 'category',
      data: ['Aug', 'Sep', 'Oct', 'Nov', 'Dec', 'Jan'],
      axisLine: {
        lineStyle: {
          color: 'rgba(0, 0, 0, 0.3)'
        }
      },
      axisLabel: {
        color: '#000000'
      }
    },
    yAxis: {
      type: 'value',
      splitLine: {
        lineStyle: {
          color: 'rgba(0, 0, 0, 0.1)'
        }
      },
      axisLabel: {
        color: '#000000'
      }
    },
    series: [
      {
        name: 'Total Users',
        type: 'bar',
        data: [35, 32, 38, 42, 36, 33],
        itemStyle: {
          color: '#7B68EE',
          borderRadius: [6, 6, 0, 0]
        },
        barWidth: '30%'
      },
      {
        name: 'New Users',
        type: 'bar',
        data: [12, 15, 17, 16, 14, 10],
        itemStyle: {
          color: '#FF8A80',
          borderRadius: [6, 6, 0, 0]
        },
        barWidth: '30%'
      }
    ],
    backgroundColor: 'transparent'
  };

  // Adoption Rate Trend Chart Configuration
  const adoptionRateData = useMemo(() => {
    const xAxisLabels = adoptionTrendData.map((item) => item.month);
    const seriesData  = adoptionTrendData.map((item) =>
      Number.parseFloat(item.adoptionRate.toFixed(2))
    );

    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'line' },
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        borderColor: '#777',
        borderWidth: 1,
        textStyle: { color: '#fff' },
        formatter: (params: any) => {
          const item = adoptionTrendData[params[0].dataIndex];
          return `
            ${params[0].name}<br/>
            Adoption Rate: <b>${params[0].value}%</b><br/>
            Active Users: <b>${item?.activeUsers ?? '-'}</b><br/>
            Total Users: <b>${item?.totalUsers ?? '-'}</b>
          `;
        }
      },
      legend: {
        data: ['Adoption Rate'],
        show: true,
        type: 'scroll',
        orient: 'horizontal',
        left: 'center',
        bottom: '8px',
        itemWidth: 14,
        itemHeight: 14,
        itemGap: 15,
        icon: 'roundRect',
        textStyle: { color: '#000000' }
      },
      grid: {
        left: '3%',
        right: '6%',
        bottom: '17%',
        top: '10%',
        containLabel: true
      },
      xAxis: {
        type: 'category',
        data: xAxisLabels,                    // ← dynamic months
        boundaryGap: false,
        axisLine: { lineStyle: { color: 'rgba(0, 0, 0, 0.3)' } },
        axisLabel: { color: '#000000' }
      },
      yAxis: {
        type: 'value',
        min: 0,
        max: 100,
        splitLine: { lineStyle: { color: 'rgba(0, 0, 0, 0.1)' } },
        axisLabel: {
          color: '#000000',
          formatter: '{value}'               // ← show % on Y axis
        }
      },
      series: [
        {
          name: 'Adoption Rate',
          type: 'line',
          smooth: true,
          data: seriesData,                   // ← dynamic values
          itemStyle: { color: '#FF69B4' },
          lineStyle: { color: '#FF69B4', width: 3 },
          showSymbol: true,
          symbolSize: 6,
          areaStyle: { color: 'transparent' }
        }
      ],
      backgroundColor: 'transparent'
    };
  }, [adoptionTrendData]);

    const fetchUserMostVisitedPageChartData = async (
    timeRange: string,
    filterMonth: string
  ) => {
    try {
      setMostVisitedPageChartLoading(true);

      const res = await fetchProductSuccessMostVisitedPage(
        timeRange,
        filterMonth
      );

      const apiData =
        res.success && res.data?.mostVisitedPages?.pageData
          ? res.data.mostVisitedPages.pageData
          : [];

      const displayData = apiData.map((item: any) => ({
        ...item,
        pageName: toPageDisplayName(item.pageName),
      }));

      setMostVisitedPageData(displayData);
    } catch (error) {
      console.error('Error fetching role usage chart:', error);
      setMostVisitedPageData([]);
    } finally {
      setMostVisitedPageChartLoading(false);
    }
  };

  // Flattened rows for the Most Visited Pages chart, including subpage rows
  // when a top-level page is expanded.
  const mostVisitedChartRows = useMemo(() => {
    const rows: { name: string; value: number; isSubpage: boolean; hasSubpages: boolean }[] = [];
    mostVisitedPageData.forEach((item: any) => {
      const hasSubpages = Boolean(item.subpages?.length);
      rows.push({ name: item.pageName, value: item.uniqueUsers, isSubpage: false, hasSubpages });
      if (expandedPage === item.pageName && item.subpages?.length) {
        item.subpages.forEach((sub: any) => {
          rows.push({ name: sub.subpageName, value: sub.uniqueUsers, isSubpage: true, hasSubpages: false });
        });
      }
    });
    return rows;
  }, [mostVisitedPageData, expandedPage]);

  // ECharts doesn't grow its own container when more y-axis categories are added — without an
  // explicit height increase, expanding a page to show its subpages causes ECharts to silently
  // skip/interlace y-axis labels so they don't overlap, hiding some subpage rows entirely.
  const MOST_VISITED_ROW_HEIGHT_PX = 26;
  const MOST_VISITED_MIN_HEIGHT_PX = 300;
  const mostVisitedChartHeight = `${Math.max(
    MOST_VISITED_MIN_HEIGHT_PX,
    mostVisitedChartRows.length * MOST_VISITED_ROW_HEIGHT_PX
  )}px`;

  const toggleExpandedPage = (pageName: string) => {
    setExpandedPage((current) => (current === pageName ? null : pageName));
  };

  useEffect(() => {
    const instance = mostVisitedChartRef.current?.getEchartsInstance?.();
    if (!instance) return;

    // convertToPixel returns coordinates relative to the ECharts canvas itself, not the
    // absolutely-positioned overlay Box (which sits inside the padded Stack container).
    // Measure the canvas's actual offset from the overlay so the toggle icons line up with
    // their bars regardless of container padding/tooltip-icon spacing above the chart.
    const chartDom = instance.getDom?.();
    const overlayNode = mostVisitedOverlayRef.current;
    const verticalOffset =
      chartDom && overlayNode
        ? chartDom.getBoundingClientRect().top - overlayNode.getBoundingClientRect().top
        : 0;

    const positions: Record<string, number> = {};
    mostVisitedChartRows.forEach((row) => {
      const px = instance.convertToPixel({ yAxisIndex: 0 }, row.name);
      if (px != null) positions[row.name] = (Array.isArray(px) ? px[1] : px) + verticalOffset;
    });
    setRowPixelPositions(positions);
  }, [mostVisitedChartRows]);

  // Most Visited Pages Chart Configuration
  const mostVisitedPagesData = {
    tooltip: getHorizontalBarTooltip((params: any) => {
      const row = mostVisitedChartRows[params[0].dataIndex];
      const label = row?.isSubpage ? 'Subpage visits' : 'Total visits (all subpages)';
      return `${row?.name ?? params[0].name}<br/>${label}: ${params[0].value}`;
    }),
    grid: HORIZONTAL_BAR_GRID,
    xAxis: HORIZONTAL_BAR_X_AXIS,
    yAxis: {
      ...getHorizontalBarYAxis(
        mostVisitedChartRows.map((row) => row.name)
      ),
      inverse: true,
      axisLabel: {
        color: '#000000',
        fontSize: 11,
        width: 160,
        overflow: 'truncate' as const,
        formatter: (value: string) => {
          const row = mostVisitedChartRows.find((r) => r.name === value);
          if (row?.isSubpage) return `— ${value}`;
          // Extra leading spaces reserve room for the expand/collapse icon overlaid
          // to the left of the label, so the icon doesn't sit flush against the text.
          return row?.hasSubpages ? `   ${value}` : value;
        },
      },
    },

    series: [
      {
        name: 'Visits',
        type: 'bar',
        data: mostVisitedChartRows.map((row) => row.value),
        itemStyle: {
          color: (params: any) => {
            const row = mostVisitedChartRows[params.dataIndex];
            return row?.isSubpage ? '#A9D9E4' : '#2EBDF1';
          },
        },
        label: {
          show: true,
          position: 'right',
          formatter: '{c}',
          color: '#000000',
          fontSize: 12
        },
        barWidth: '65%'
      }
    ],
    backgroundColor: 'transparent'
  };

  const fetchUserRoleUsageChartData = async (
    timeRange: string,
    filterMonth: string
  ) => {
    try {
      setUserRoleUsageChartLoading(true);

      const res = await fetchProductSuccessUsersRoleUsageTrend(
        timeRange,
        filterMonth
      );

      const apiData =
        res.success && res.data?.usersRoleUsageTrend?.userRoleData
          ? res.data.usersRoleUsageTrend.userRoleData
          : [];

      setRoleData(apiData);
    } catch (error) {
      console.error('Error fetching role usage chart:', error);
      setRoleData([]);
    } finally {
      setUserRoleUsageChartLoading(false);
    }
  };

  const toggleExpandedRole = (role: string) => {
    setExpandedRole((current) => (current === role ? null : role));
  };

  const expandedRoleUsers = useMemo(() => {
    if (!expandedRole) return [];
    const role = roleData.find((item) => item.role === expandedRole);
    return [...(role?.users ?? [])].sort((a, b) => a.displayName.localeCompare(b.displayName));
  }, [roleData, expandedRole]);

  const LARGE_ROLE_USER_THRESHOLD = 100;
  const rowsPerColumn = expandedRoleUsers.length > LARGE_ROLE_USER_THRESHOLD ? 10 : 5;

  const roleUsersGridColumns = useMemo(() => {
    if (expandedRoleUsers.length === 0) return [];

    const columnCount = Math.ceil(expandedRoleUsers.length / rowsPerColumn);
    const columns: RoleUserItem[][] = Array.from({ length: columnCount }, () => []);
    expandedRoleUsers.forEach((user, index) => {
      columns[Math.floor(index / rowsPerColumn)].push(user);
    });
    return columns;
  }, [expandedRoleUsers, rowsPerColumn]);

  const sortedRoleData = useMemo(
    () => [...roleData].sort((a, b) => b.count - a.count),
    [roleData]
  );

  const ROLE_CHART_ROW_HEIGHT_PX = 26;
  const SPACER_ROWS_NEEDED = expandedRoleUsers.length > LARGE_ROLE_USER_THRESHOLD ? 12 : 7;
  const ROLE_USERS_PANEL_HEIGHT_PX = SPACER_ROWS_NEEDED * ROLE_CHART_ROW_HEIGHT_PX;
  const EXPANDED_ROLE_SPACER_PREFIX = '__expanded_role_spacer__';

  const roleChartRows = useMemo(() => {
    const rows: { role: string; count: number | null; isSpacer: boolean }[] = [];
    sortedRoleData.forEach((item) => {
      rows.push({ role: item.role, count: item.count, isSpacer: false });
      if (expandedRole === item.role) {
        for (let i = 0; i < SPACER_ROWS_NEEDED; i += 1) {
          rows.push({ role: `${EXPANDED_ROLE_SPACER_PREFIX}${i}`, count: null, isSpacer: true });
        }
      }
    });
    return rows;
  }, [sortedRoleData, expandedRole]);

  const ROLE_CHART_MIN_HEIGHT_PX = 300;
  const roleChartHeight = `${Math.max(
    ROLE_CHART_MIN_HEIGHT_PX,
    roleChartRows.length * ROLE_CHART_ROW_HEIGHT_PX
  )}px`;

  useEffect(() => {
    const instance = roleChartRef.current?.getEchartsInstance?.();
    if (!instance) return;

    const measure = () => {
      instance.resize();

      const chartDom = instance.getDom?.();
      const overlayNode = roleOverlayRef.current;
      const verticalOffset =
        chartDom && overlayNode
          ? chartDom.getBoundingClientRect().top - overlayNode.getBoundingClientRect().top
          : 0;

      const positions: Record<string, number> = {};
      roleChartRows.forEach((row) => {
        const px = instance.convertToPixel({ yAxisIndex: 0 }, row.role);
        if (px != null) positions[row.role] = (Array.isArray(px) ? px[1] : px) + verticalOffset;
      });
      setRolePixelPositions(positions);
    };

    const rafId = requestAnimationFrame(measure);
    return () => cancelAnimationFrame(rafId);
  }, [roleChartRows]);

  const roleOfUsersData = useMemo(() => {
    const yAxisLabels = roleChartRows.map((row) => row.role);

    const chartData = roleChartRows.map((row) => ({
      value: row.count,
      itemStyle: {
        color: '#2EBDF1'
      }
    }));

    return {
      tooltip: getHorizontalBarTooltip((params: any) => {
        const row = roleChartRows[params[0].dataIndex];
        if (row?.isSpacer) return '';
        return `${row?.role ?? params[0].name}<br/>${params[0].value} users`;
      }),
      grid: HORIZONTAL_BAR_GRID,
      xAxis: HORIZONTAL_BAR_X_AXIS,
      yAxis: {
        ...getHorizontalBarYAxis(yAxisLabels),
        inverse: true,
        axisLabel: {
          color: '#000000',
          fontSize: 11,
          width: 160,
          overflow: 'truncate' as const,
          formatter: (value: string) => {
            if (value.startsWith(EXPANDED_ROLE_SPACER_PREFIX)) return '';
            // Extra leading spaces reserve room for the expand/collapse icon overlaid
            // to the left of the label, so the icon doesn't sit flush against the text.
            return `   ${value}`;
          },
        },
      },
      series: [
        {
          name: 'Users',
          type: 'bar',
          data: chartData,
          label: {
            show: true,
            position: 'right',
            formatter: (params: any) => {
              const row = roleChartRows[params.dataIndex];
              return row?.isSpacer ? '' : `${params.value}`;
            },
            color: '#000000',
            fontSize: 12
          },
          barWidth: '65%'
        }
      ],
      backgroundColor: 'transparent'
    };
  }, [roleChartRows]);

  const formatSeconds = (seconds: number) => {
    const totalSeconds = Math.round(seconds);

    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const secs = totalSeconds % 60;

    if (hours > 0) {
      return `${hours}h ${minutes}m ${secs}s`;
    }

    return `${minutes}m ${secs}s`;
  };

  const fetchUserAvgTimeInPlatform = async (
    timeRange: string,
    filterMonth: string
  ) => {
    try {
      setLoadingAvgSession(true);

      const response = await fetchProductSuccessUserAvgTimeInPlatform(timeRange, filterMonth);

      const kpiData = response?.data?.usersAvgTimeInPlatform;

      setAvgSessionData(kpiData);

    } catch (error) {
      console.error("Error fetching avg session duration:", error);
    } finally {
      setLoadingAvgSession(false);
    }
  };

  const fetchUserAdoptionRate = async (
    timeRange: string,
    filterMonth: string
  ) => {
    try {
      setLoadingAdoptionRate(true);

      const response = await fetchProductSuccessUserAdoptionRate(timeRange, filterMonth);

      const kpiData = response?.data?.usersAdoptionRate;

      setAdoptionRate(kpiData);

    } catch (error) {
      console.error("Error fetching avg session duration:", error);
    } finally {
      setLoadingAdoptionRate(false);
    }
  };

  return (
    <PageWrapper sx={{ paddingTop: {xs:'14px !important', sm:'34px !important', md:'30px !important'} }}>
      
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        gap={2}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        justifyContent={"end"}
        sx={{ width: { xs: '100%', md: 'auto' }, zIndex: 0, mb: 3 }}
      >
        <SimpleDateFilter
          filterMonth={timeRange}
          setFilterMonth={setTimeRange}
          handleChange={handleDateChange}
          renderValue={showTimeRange}
          customOptions={TIME_FILTER_OPTIONS}
          boxProps={{
            sx: {
              minWidth: { xs: 'auto', md: '13.75rem' },
              width: { xs: '100%', sm: 'auto' },
              maxWidth: '100%',
              backgroundColor: '#143968ff',
              '& .MuiSelect-select': {
                color: '#ffffff',
              },
            }
          }}
        />

        <Box sx={{ width: { xs: '100%', sm: 'auto' } }}>
          <YearMonthPicker 
                customMonth={customMonth}
                filterMonth={filterMonth}
                setCustomMonth={setCustomMonth}
                setFilterMonth={setFilterMonth}
                handleChange={handleFilterChange}
            />
        </Box>
      </Stack>
      <Box sx={{mb: 2}}>
        <Typography variant="subHeader" sx={{ whiteSpace: 'nowrap', fontWeight: 'bold', fontSize: '18px', color: `${theme.palette.table.title}` }} >Summary</Typography>
      </Box>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            isLoading={kpiLoading}
            title="Total Users"
            hasTooltip
            tooltipLabel="Total unique users who logged into PepWise during the selected period"
            number={kpiData?.totalUsers?.value ?? '-'}
            isCost={false}
            showChangePercentage={true}
            change={kpiData?.totalUsers?.change ?? 0}
            changeType={kpiData?.totalUsers?.changeType ?? 'increase'}
            showPeriodChange={false}
            reverseTrendColor={false}
           />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            isLoading={false}
            title="Top Users"
            hasTooltip
            tooltipLabel="Number of highest-frequency users based on login activity in the selected period"
            number={32}
            isCost={false}
            showChangePercentage={true}
            change={45.76}
            changeType="decrease"
            showPeriodChange={false}
            reverseTrendColor={false}
            hasModal={true}
            modalText="View Users"
            onModalClick={() => setActiveUsersModalOpen(true)}
           />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            isLoading={loadingAdoptionRate}
            title="Adoption Rate"
            hasTooltip
            tooltipLabel="Percentage of active users relative to total eligible users for the selected period"
            number={adoptionRate?.value ?? '-'}
            isCost={false}
            isPercentage={true}
            showChangePercentage={true}
            change={adoptionRate?.change ?? 0}
            changeType={adoptionRate?.changeType ?? 'increase'}
            showPeriodChange={false}
            reverseTrendColor={false}
           />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            isLoading={kpiLoading}
            title="Average Logins per Week"
            hasTooltip
            tooltipLabel="Average number of times users logged in per week during the selected period"
            number={kpiData?.avgLoginsPerWeek?.value ?? '-'}
            isCost={false}
            showChangePercentage={true}
            change={kpiData?.avgLoginsPerWeek?.change ?? 0}
            changeType={kpiData?.avgLoginsPerWeek?.changeType ?? 'neutral'}
            showPeriodChange={false}
            reverseTrendColor={false}
           />
        </Grid>
        <Grid item xs={12} sm={6}>
          <KpiCard
            isLoading={false}
            title="New Users per Week"
            hasTooltip
            tooltipLabel="Percentage of first-time users joining PepWise each week in the selected period"
            number={16}
            isPercentage={true}
            isCost={false}
            showChangePercentage={true}
            change={5.32}
            showPeriodChange={false}
            reverseTrendColor={false}
           />
        </Grid>
        <Grid item xs={12} sm={6}>
          <KpiCard
            isLoading={loadingAvgSession}
            title="Average Time in Platform"
            hasTooltip
            tooltipLabel="Average session duration per user during the selected period"
            number={avgSessionData?.value ? formatSeconds(avgSessionData.value) : '-'}
            isCost={false}
            showChangePercentage={true}
            change={avgSessionData?.change ?? 0}
            showPeriodChange={false}
            reverseTrendColor={false}
            changeType={avgSessionData?.changeType ?? 'increase'} 
           />
        </Grid>
      </Grid>
      <Box sx={{mb: 2}}>
        <Typography variant="subHeader" sx={{ whiteSpace: 'nowrap', fontWeight: 'bold', fontSize: '18px', color: '#000000' }} >Adoption and Usage Trends</Typography>
      </Box>
      <Grid container spacing={2} sx={{ mb: 1 }}>
        <Grid item xs={12} sm={6}>
          <Box sx={{ 
            borderRadius: '5px',
            height: '100%',
            position: 'relative'
          }}>
            <Box sx={{
              position: 'absolute',
              top: 24,
              right: 16,
              zIndex: 10
            }}>
              {renderTooltipIconComponent('Monthly trend of total users vs new users to track growth and engagement', '#000000')}
            </Box>
            <MultiLineChart
              title="User Engagement"
              isLoading={false}
              data={userEngagementData}
              containerColor="#ffffff"
              titleColor="#000000"
            />
          </Box>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Box sx={{ 
            borderRadius: '5px',
            height: '100%',
            position: 'relative'
          }}>
            <Box sx={{
              position: 'absolute',
              top: 24,
              right: 16,
              zIndex: 10
            }}>
              {renderTooltipIconComponent('Monthly movement of adoption rate to monitor platform penetration over time', '#000000')}
            </Box>
            <MultiLineChart
              title="Adoption Rate"
              isLoading={adoptionTrendLoading}
              data={adoptionRateData}
              containerColor="#ffffff"
              titleColor="#000000"
            />
          </Box>
        </Grid>
      </Grid>
      <Grid container spacing={2} sx={{ mb: 3, mt: 1 }}>
        <Grid item xs={12} sm={6}>
          <WidgetWraper>
            <Stack direction="row" alignItems="center">
              <Typography variant="widgetTitleNew" sx={{ color: theme.palette.widget.text.title }}>
                Most Visited Pages
              </Typography>
              <InfoTooltip label="Count of page visits to identify high-traffic modules" />
            </Stack>
            <Box flexGrow={1} position="relative" mt={2}>
              <Box flexGrow={1}>
                <ReactECharts
                  ref={mostVisitedChartRef}
                  option={mostVisitedPagesData}
                  style={{ height: '100%', minHeight: mostVisitedChartHeight }}
                  showLoading={mostVisitedPageChartLoading}
                />
              </Box>
              <Box ref={mostVisitedOverlayRef} sx={{ position: 'absolute', left: 8, top: 0, bottom: 0, zIndex: 2 }}>
                {mostVisitedChartRows.map((row) =>
                  row.isSubpage || !row.hasSubpages || rowPixelPositions[row.name] == null ? null : (
                    <Box
                      key={row.name}
                      data-testid={`most-visited-toggle-${row.name}`}
                      role="button"
                      aria-label={expandedPage === row.name ? `Collapse ${row.name}` : `Expand ${row.name}`}
                      onClick={() => toggleExpandedPage(row.name)}
                      sx={{
                        position: 'absolute',
                        top: rowPixelPositions[row.name] - 10,
                        cursor: 'pointer',
                        color: '#003771',
                        display: 'flex',
                      }}
                    >
                      {expandedPage === row.name ? (
                        <RemoveIcon fontSize="small" sx={{ color: 'inherit' }} />
                      ) : (
                        <AddIcon fontSize="small" sx={{ color: 'inherit' }} />
                      )}
                    </Box>
                  )
                )}
              </Box>
            </Box>
          </WidgetWraper>
        </Grid>
        <Grid item xs={12} sm={6}>
          <WidgetWraper>
            <Stack direction="row" alignItems="center">
              <Typography variant="widgetTitleNew" sx={{ color: theme.palette.widget.text.title }}>
                Role of Users
              </Typography>
              <InfoTooltip label="Breakdown of active users by role to understand persona-level adoption" />
            </Stack>
            <Box flexGrow={1} position="relative" mt={2}>
              <ReactECharts
                ref={roleChartRef}
                option={roleOfUsersData}
                style={{ height: '100%', minHeight: roleChartHeight }}
                showLoading={userRoleUsageChartLoading}
              />
            </Box>
            <Box ref={roleOverlayRef} sx={{ position: 'absolute', left: 8, right: 16, top: 0, bottom: 0, zIndex: 2, pointerEvents: 'none' }}>
              {sortedRoleData.map((item) =>
                rolePixelPositions[item.role] == null ? null : (
                  <Box
                    key={item.role}
                    data-testid={`role-of-users-toggle-${item.role}`}
                    role="button"
                    aria-label={expandedRole === item.role ? `Collapse ${item.role}` : `Expand ${item.role}`}
                    onClick={() => toggleExpandedRole(item.role)}
                    sx={{
                      position: 'absolute',
                      top: rolePixelPositions[item.role] - 10,
                      left: 0,
                      cursor: 'pointer',
                      color: '#003771',
                      display: 'flex',
                      pointerEvents: 'auto',
                    }}
                  >
                    {expandedRole === item.role ? (
                      <RemoveIcon fontSize="small" sx={{ color: 'inherit' }} />
                    ) : (
                      <AddIcon fontSize="small" sx={{ color: 'inherit' }} />
                    )}
                  </Box>
                )
              )}
              {expandedRole && rolePixelPositions[`${EXPANDED_ROLE_SPACER_PREFIX}0`] != null && (() => {
                const firstSpacerPx = rolePixelPositions[`${EXPANDED_ROLE_SPACER_PREFIX}0`];
                const secondSpacerPx = rolePixelPositions[`${EXPANDED_ROLE_SPACER_PREFIX}1`];
                const lastSpacerPx = rolePixelPositions[`${EXPANDED_ROLE_SPACER_PREFIX}${SPACER_ROWS_NEEDED - 1}`];
                const realRowHeight = secondSpacerPx != null ? secondSpacerPx - firstSpacerPx : ROLE_CHART_ROW_HEIGHT_PX;
                const panelTop = firstSpacerPx - realRowHeight / 2;
                const panelHeight = lastSpacerPx != null ? lastSpacerPx - firstSpacerPx + realRowHeight : ROLE_USERS_PANEL_HEIGHT_PX;

                return (
                <Box
                  data-testid="role-of-users-panel"
                  sx={{
                    position: 'absolute',
                    top: panelTop,
                    left: 0,
                    right: 0,
                    height: `${panelHeight}px`,
                    maxHeight: `${panelHeight}px`,
                    boxSizing: 'border-box',
                    backgroundColor: '#ffffff',
                    border: '1px solid rgba(0, 0, 0, 0.1)',
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                    px: 2,
                    pt: 0.5,
                    pb: 0.5,
                    pointerEvents: 'auto',
                  }}
                >
                  <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#000000', mb: 0.5, flexShrink: 0 }}>
                    Users in "{expandedRole}" ({expandedRoleUsers.length})
                  </Typography>
                  {expandedRoleUsers.length === 0 ? (
                    <Typography sx={{ fontSize: '12px', color: 'rgba(0,0,0,0.6)' }}>
                      No users to display
                    </Typography>
                  ) : (
                    <Box
                      data-testid="role-of-users-grid"
                      sx={{
                        display: 'flex',
                        gap: 2,
                        minHeight: 0,
                        pb: '20px',
                        boxSizing: 'border-box',
                        overflowX: 'auto',
                        overflowY: 'hidden',
                      }}
                    >
                      {roleUsersGridColumns.map((column, columnIndex) => (
                        <Box
                          key={columnIndex}
                          sx={{
                            flex: '0 0 160px',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 0.5,
                          }}
                        >
                          {column.map((user) => (
                            <Typography
                              key={user.indvId}
                              title={user.email ?? undefined}
                              sx={{
                                fontSize: '12px',
                                color: '#000000',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                flexShrink: 0,
                              }}
                            >
                              {user.displayName}
                            </Typography>
                          ))}
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
                );
              })()}
            </Box>
          </WidgetWraper>
        </Grid>
      </Grid>

      {/* Active Users Modal */}
      <DialogueModal
        title="Active Users"
        isOpen={activeUsersModalOpen}
        handleClose={() => setActiveUsersModalOpen(false)}
        width="lg"
        dialogContentProps={{ sx: { textAlign: 'left' } }}
        titleSx={{ px: '20px', py: '16px' }}
      >
        <Box sx={{
          px: 3,
          py: 4,
          backgroundColor: '#ffffff',
          '& .rdg-header-row .rdg-cell': {
            backgroundColor: '#003771 !important',
            display: 'flex !important',
            alignItems: 'center !important',
            color: '#ffffff !important',
            fontWeight: 600,
            borderRight: '1px solid rgba(255,255,255,0.15)',
            '> div': {
              width: '100% !important',
            }
          },
          '& .rdg-row:nth-of-type(odd)': {
            backgroundColor: '#ffffff !important',
          },
          '& .rdg-row:nth-of-type(even)': {
            backgroundColor: '#EBF5FF !important',
          },
          '& .rdg-row .rdg-cell': {
            color: '#000000 !important',
            borderRight: 'none !important',
            display: 'flex !important',
            alignItems: 'center !important',
          },
          '& .rdg-row:hover .rdg-cell': {
            backgroundColor: '#d6eaff !important',
          },
        }}>
          <CustomizedDataGrid
            tableColumns={ACTIVE_USERS_COLUMNS}
            tableData={ACTIVE_USERS_DATA}
            tableType="activeUsers"
            defaultColumnNames={['USER NAME', 'ACTIVE TIME IN PLATFORM', 'ROLE']}
            page={1}
            pageSize={6}
            paginationData={{ totalPages: 1, currentPage: 1, totalElements: 6 }}
            listLength={6}
            isFetching={false}
            isRenderPagination={true}
            isShowMoreButton={false}
            autoSetDraggedColumns={false}
            isLighterTheme={true}
            rowHeight={45}
            headerRowHeight={45}
            style={{ minHeight: '300px', maxHeight: '400px' }}
            onSorting={() => {}}
            currSorting={{}}
            onFilterCheck={() => {}}
            onFilterSearch={() => {}}
            onHeaderSelectAll={() => {}}
            onFilterClick={() => {}}
            handlepageSizeChange={() => {}}
            handlePageChange={() => {}}
          />
        </Box>
      </DialogueModal>
    </PageWrapper>
  )
}

export default ProductSuccessDashboard